import { useMemo, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { SalesforceBundleGroup } from "~/api/models/Bundle";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import { useFetchProducts } from "~/api/salesForce/useFetchProducts";
import SpinnerIcon from "~/components/atoms/SpinnerIcon/SpinnerIcon";
import TabsToggle from "~/components/molecules/TabsToggle/TabsToggle";
import BundleCard from "~/components/organisms/BundleCard/BundleCard";
import CosmeticProductCard from "~/components/organisms/CosmeticCard/CosmeticProductCard";
import { Header } from "~/components/organisms/layout/Header/Header";
import { useBundles } from "~/hooks/useBundles";
import useCosmeticProducts from "~/hooks/useCosmeticProducts";
import useSkinBundles from "~/hooks/useSkinBundles";

enum CosmeticsTabOptions {
  Bundles = "Bundles",
  Products = "Products",
}

const Cosmetics = () => {
  const intl = useIntl();
  const { opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity({
    id: opportunityId as string,
  });
  const { bundles, isLoading, selectedBundles } = useBundles();
  const { setSkinBundleAndHandleSkinCategory } = useSkinBundles();
  const { data: allProducts, isLoading: isProductsLoading } =
    useFetchProducts();
  const { toggleCosmeticProduct, isCosmeticProductSelected } =
    useCosmeticProducts();

  const cosmeticBundles = useMemo(() => {
    if (!bundles) return [];
    const filtered = bundles.filter(
      (bundle) => bundle.group === SalesforceBundleGroup.Cosmetic // Show ONLY cosmetic bundles
    );
    return filtered;
  }, [bundles]);

  const cosmeticProducts = useMemo(() => {
    if (!allProducts) return [];
    return allProducts.cosmetic;
  }, [allProducts]);

  const tabOptions = useMemo(() => {
    return Object.values(CosmeticsTabOptions).map((group) => ({
      key: group,
      value: group,
    }));
  }, []);

  const [activeTab, setActiveTab] = useState<CosmeticsTabOptions | null>(null);

  useEffect(() => {
    if (tabOptions.length > 0 && !activeTab) {
      setActiveTab(tabOptions[0].key);
    }
  }, [tabOptions, activeTab]);

  if (isLoading || isProductsLoading) {
    return (
      <div className="gap-header p-body flex flex-col h-full relative">
        <Header title={intl.formatMessage({ id: "cosmetics.title" })} />
        <div className="w-full h-full flex items-center justify-center">
          <SpinnerIcon />
        </div>
      </div>
    );
  }

  return (
    <div className="gap-header p-body flex flex-col h-full relative">
      <Header title={intl.formatMessage({ id: "cosmetics.title" })} />
      {tabOptions.length > 1 && (
        <TabsToggle
          options={tabOptions}
          activeOption={activeTab}
          setActiveOption={setActiveTab}
        />
      )}
      <div className="grid grid-cols-3 gap-6 w-full overflow-y-auto pb-10">
        {activeTab === CosmeticsTabOptions.Bundles &&
          cosmeticBundles.map((bundle) => (
            <BundleCard
              key={bundle.id}
              bundle={bundle}
              onSelect={() => {
                setSkinBundleAndHandleSkinCategory(bundle);
              }}
              isSelected={selectedBundles.some(
                (selectedBundle) => selectedBundle.id === bundle.id
              )}
              isRecommended={false}
              disabled={!opportunity?.isValidForSales}
            />
          ))}
        {activeTab === CosmeticsTabOptions.Products && (
          <div className="col-span-3 grid grid-cols-1 gap-6">
            {cosmeticProducts.map((product) => (
              <CosmeticProductCard
                key={product.id}
                product={product}
                onAdd={() => {
                  toggleCosmeticProduct(product);
                }}
                isSelected={isCosmeticProductSelected(product.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Cosmetics;
